import React from "react"
import { Link } from "gatsby"

import Layout from "../components/layout"
import SEO from "../components/seo"
import LegalLayout from "../components/legallayout"
import ContactInfo from "../components/contactinfo"

const PrivacyPage = () => (
  <Layout>
    <SEO title="Privacy Policy" />
    <LegalLayout>
      <h1>Privacy Policy</h1>
      <p>
        <strong>Effective Date:</strong> 05/11/2026
        <br />
        <strong>Last Updated:</strong> 05/11/2026
      </p>
      <p>
        Wiltshire Jones ("we," "us," or "our") respects your privacy. This
        Privacy Policy explains how we collect, use, disclose, and protect your
        information when you visit our website, use our services, or opt in to
        receive text messages from us.
      </p>

      <h2>1. Information We Collect</h2>
      <p>We may collect the following categories of information:</p>
      <ul>
        <li>
          <strong>Contact information</strong> such as your name, email address,
          mailing address, and mobile phone number
        </li>
        <li>
          <strong>Transaction information</strong> such as purchases, orders,
          and billing details
        </li>
        <li>
          <strong>Communications</strong> including messages you send us and SMS
          opt-in records
        </li>
        <li>
          <strong>Technical information</strong> such as IP address, device
          type, browser, and usage data
        </li>
        <li>
          <strong>Location information</strong> based on IP address or device
          settings, where available
        </li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Provide, operate, and improve our products and services</li>
        <li>Process transactions and send related confirmations</li>
        <li>Send text messages you have opted in to receive</li>
        <li>Respond to inquiries and provide customer support</li>
        <li>Send administrative information, such as policy updates</li>
        <li>Send marketing communications where permitted by law</li>
        <li>Detect, prevent, and address fraud or security issues</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2>3. SMS Messaging and Mobile Information</h2>
      <p>
        If you opt in to receive text messages from us, we collect your mobile
        phone number, the date and method of your opt-in, and records of
        messages sent and received.
      </p>
      <p>
        <strong>
          No mobile information will be shared with third parties or affiliates
          for marketing or promotional purposes. All other categories exclude
          text messaging originator opt-in data and consent; this information
          will not be shared with any third parties.
        </strong>
      </p>
      <p>
        We may share your mobile information with service providers who help us
        operate the SMS program (such as our messaging platform provider) under
        contractual obligations to protect your data and use it only to deliver
        the service. These providers are not permitted to use your mobile
        information for their own marketing purposes.
      </p>
      <p>
        For information on opting out of SMS messages, see our{" "}
        <Link to="/terms">SMS Terms and Conditions</Link>.
      </p>

      <h2>4. How We Share Information</h2>
      <p>Aside from the SMS-specific restrictions above, we may share your information with:</p>
      <ul>
        <li>
          <strong>Service providers</strong> who perform services on our behalf
          (payment processing, hosting, analytics, customer support, email/SMS
          delivery), under confidentiality obligations
        </li>
        <li>
          <strong>Business partners</strong> with your consent or as necessary
          to fulfill a transaction you have requested
        </li>
        <li>
          <strong>Legal authorities</strong> when required by law, subpoena,
          court order, or to protect our legal rights
        </li>
        <li>
          <strong>Successors</strong> in the event of a merger, acquisition,
          financing, or sale of assets, subject to confidentiality protections
        </li>
        <li>
          <strong>With your consent</strong> for any other purpose disclosed at
          the time you provide the information
        </li>
      </ul>
      <p>We do not sell your personal information for monetary consideration.</p>

      <h2>5. Data Retention</h2>
      <p>
        We retain your personal information for as long as necessary to provide
        the services you have requested, comply with our legal obligations,
        resolve disputes, and enforce our agreements. SMS opt-in records are
        retained for at least the period required by applicable law (typically
        four years for TCPA compliance in the United States).
      </p>

      <h2>6. Data Security</h2>
      <p>
        We use reasonable administrative, technical, and physical safeguards to
        protect your information. However, no system is completely secure, and
        we cannot guarantee absolute security of information transmitted over
        the internet or via SMS.
      </p>

      <h2>7. Your Rights and Choices</h2>
      <p>Depending on your jurisdiction, you may have the right to:</p>
      <ul>
        <li>Access the personal information we hold about you</li>
        <li>Request correction of inaccurate information</li>
        <li>Request deletion of your information</li>
        <li>Opt out of marketing communications</li>
        <li>Withdraw consent where processing is based on consent</li>
        <li>Lodge a complaint with a supervisory authority</li>
      </ul>
      <p>
        <strong>To opt out of SMS messages</strong>, reply STOP to any message
        you receive from us.
      </p>
      <p>
        <strong>To opt out of email marketing</strong>, click the unsubscribe
        link in any marketing email or contact us directly.
      </p>
      <p>
        <strong>To exercise other rights</strong>, contact us using the
        information in Section 10.
      </p>

      <h2>8. State-Specific Privacy Rights</h2>
      <p>
        Residents of California, Virginia, Colorado, Connecticut, Utah, and
        other states with comprehensive privacy laws may have additional rights,
        including the right to know what personal information we collect, the
        right to delete personal information, and the right to opt out of
        certain processing. Contact us to exercise these rights.
      </p>

      <h2>9. Children&#39;s Privacy</h2>
      <p>
        Our services are not directed to children under 13, and we do not
        knowingly collect personal information from children under 13. If we
        learn we have collected such information, we will delete it promptly.
      </p>

      <h2>10. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or our privacy
        practices, contact us at:
      </p>
      <ContactInfo email="privacy@wiltshirejones.com" />

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. The "Last Updated"
        date at the top reflects the most recent revision. Material changes will
        be communicated by email, by notice on our website, or by other means as
        required by law. Continued use of our services after changes are posted
        constitutes acceptance.
      </p>
    </LegalLayout>
  </Layout>
)

export default PrivacyPage
